import { useState } from 'react';
import { Trans } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { Language } from '@anytag/shared-types';
import { Anchor, BaseModal } from '@anytag/shared-atoms';
import { useQueryHelper, useToggleState } from '@anytag/shared-hooks';
import {
  namedOperations,
  SocialAccountType,
  useRefreshProfileAccountMutation,
  RefreshProfileAvailabilityStatus,
  useRefreshProfileAccountAvailabilityQuery
} from '@anytag/graphql';
import { RefreshProfileButton } from './refresh-profile-button';

interface DefaultRefreshButtonProps {
  className?: string;
  socialAccountId: number;
  socialMedia: SocialAccountType;
}
export const DefaultRefreshButton = ({ socialMedia, socialAccountId, className }: DefaultRefreshButtonProps) => {
  const noDataModal = useToggleState();
  const { isAdminStaff } = useUserRoles();
  const { t, i18n, enqueueSnackbar } = useQueryHelper();
  const [disableAfterUpdate, setDisableAfterUpdate] = useState(false); // callRefreshProfileAccount can be a bit slow to update on refetchQueries, after each success update we can set this flag to hide the button
  const { data, loading: checkingAvailability } = useRefreshProfileAccountAvailabilityQuery({
    fetchPolicy: 'network-only',
    skip: socialMedia === SocialAccountType.XHS,
    variables: { input: { socialMedia, socialAccountId } }
  });
  const { callRefreshProfileAccount, loading } = useRefreshProfileAccountMutation({
    refetchQueries: [namedOperations.Query.RefreshProfileAccountAvailability]
  });

  const status = data?.refreshProfileAccountAvailability?.status;

  const handleRefresh = async () => {
    if (!status) return;

    if (status === RefreshProfileAvailabilityStatus.NO_DATA) {
      noDataModal.open();

      return;
    }

    try {
      await callRefreshProfileAccount({
        variables: { input: { socialMedia, socialAccountId } }
      });

      enqueueSnackbar(t('Account data refreshed successfully'), { variant: 'success' });
      setDisableAfterUpdate(true);
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  return !checkingAvailability && status ? (
    <>
      <RefreshProfileButton
        loading={loading}
        className={className}
        onClick={handleRefresh}
        disabled={disableAfterUpdate || status === RefreshProfileAvailabilityStatus.NOT_AVAILABLE}
      />

      <BaseModal
        open={noDataModal.status}
        onClose={noDataModal.close}
        title={t('Failed to update the profile data')}
        footer={{ cancelButton: { onClick: noDataModal.close } }}
        description={
          <>
            <p>
              <Trans
                components={{ br: <br /> }}
                i18nKey={isAdminStaff ? 'refreshProfileDescriptionForAdmin' : 'refreshProfileDescription'}
              />
            </p>
            {isAdminStaff ? (
              <p>
                <Trans
                  i18nKey="refreshProfileDescriptionDownloadExtension"
                  components={{
                    a: (
                      <Anchor
                        variant="blue"
                        target="_blank"
                        css={{ display: 'inline-flex' }}
                        href={
                          i18n.language === Language.ja
                            ? 'https://anymind-group.gitbook.io/anytag-1/infuruens/ekusutenshon'
                            : 'https://anymind-group.gitbook.io/english-1/infuruens/extension'
                        }
                      />
                    )
                  }}
                />
              </p>
            ) : null}
          </>
        }
      />
    </>
  ) : null;
};
